import { am } from './utils';

import httpClient, { jsonObjectToFormData } from '@/utils/httpClient';
import { getDocumentTypeLabel, normalizeDocumentTypeKey } from '@/components/inventions/filesPageParts/documentTypes';

const INVENTION_FILES_LOAD = am(`INVENTION_FILES_LOAD`);
const GET_FILE_TOKEN = am(`GET_FILE_TOKEN`);
const LOAD_DOCUMENT_TYPES = am(`LOAD_DOCUMENT_TYPES`);

export default {
  namespaced: true,
  state: {
    files: [],
    fileToken: null,
    isLoadingFiles: true,
    isGettingTokenInProgress: false,
    documentTypes: [],
    isLoadingDocumentTypes: false,
    uploadingDocs: []
  },

  mutations: {
    [INVENTION_FILES_LOAD.STARTED](state) {
      state.isLoadingFiles = true;
      state.files = [];
    },
    [INVENTION_FILES_LOAD.COMPLETED](state, files) {
      state.isLoadingFiles = false;
      state.files = files;
    },
    [GET_FILE_TOKEN.STARTED](state) {
      state.isGettingTokenInProgress = true;
    },
    [GET_FILE_TOKEN.COMPLETED](state, fileToken) {
      state.isGettingTokenInProgress = false;
      state.fileToken = fileToken;
    },
    tagDeleted(state, { fileId, tag }) {
      const file = state.files.find(file => file.id === fileId);
      file.tags = file.tags.filter(t => t !== tag);
    },
    filesUploaded(state, newDocs) {
      const existingIds = new Set(state.files.map(f => f.id));
      state.files = [...state.files, ...newDocs.filter(d => !existingIds.has(d.id))];
    },
    documentUpdated(state, document) {
      const fileIndex = state.files.findIndex(file => file.id === document.id);
      state.files[fileIndex] = { ...state.files[fileIndex], ...document };
    },
    documentDeleted(state, documentId) {
      const doc = state.files.find(file => file.id === documentId);
      doc.isDeleted = true;
    },
    [LOAD_DOCUMENT_TYPES.STARTED](state) {
      state.isLoadingDocumentTypes = true;
    },
    [LOAD_DOCUMENT_TYPES.COMPLETED](state, documentTypes) {
      state.isLoadingDocumentTypes = false;
      state.documentTypes = documentTypes;
    },
    uploadingDocAdded(state, placeholder) {
      state.uploadingDocs.push(placeholder);
    },
    uploadingDocRemoved(state, id) {
      state.uploadingDocs = state.uploadingDocs.filter(d => d._id !== id);
    }
  },
  actions: {
    async getToken({ commit }, id) {
      commit(GET_FILE_TOKEN.STARTED);
      const fileToken = await httpClient.post('/api/filesmanager/token', {
        path: `/files/${id}/download`
      });
      commit(GET_FILE_TOKEN.COMPLETED, fileToken);
      return fileToken;
    },
    async loadFiles({ commit }, references) {
      commit(INVENTION_FILES_LOAD.STARTED);
      const files = await httpClient.post('/api/filesmanager/files/getbyreference', {
        reference: references
      });

      commit(INVENTION_FILES_LOAD.COMPLETED, files);
      return files;
    },
    async updateDocument({ commit }, { id, document }) {
      const doc = await httpClient.patch(`/api/filesmanager/files/${id}`, {
        ...document
      });
      commit('documentUpdated', doc);
    },
    async uploadFiles({ commit, dispatch }, payload) {
      const placeholder = {
        _id: `${Date.now()}-${Math.random()}`,
        name: payload.documentName || (payload.files && payload.files[0] && payload.files[0].name) || 'Uploading...',
        tags: payload.tags
      };
      commit('uploadingDocAdded', placeholder);
      try {
        const formPayload = { ...payload };
        if (formPayload.parsingOptions) {
          formPayload.parsingOptions = JSON.stringify(formPayload.parsingOptions);
        }
        const newDocs = await httpClient.request('/api/filesmanager/files/file', {
          json: false,
          method: 'POST',
          body: jsonObjectToFormData(formPayload)
        });
        if (Array.isArray(newDocs)) {
          commit('filesUploaded', newDocs);
        }
      } finally {
        commit('uploadingDocRemoved', placeholder._id);
      }
    },
    async markFilesDeleted({ commit }, documentIds) {
      for (const documentId of documentIds) {
        await httpClient.delete(`/api/filesmanager/files/${documentId}`);
        commit('documentDeleted', documentId);
      }
    },
    async loadDocumentTypes({ commit }) {
      commit(LOAD_DOCUMENT_TYPES.STARTED);
      const types = await httpClient.get('/api/filesmanager/files/types');
      commit(
        LOAD_DOCUMENT_TYPES.COMPLETED,
        Object.keys(types)
          .filter(key => normalizeDocumentTypeKey(key) !== 'none')
          .sort()
          .map(key => ({
            key: key.toLocaleLowerCase(),
            value: getDocumentTypeLabel(key, types[key])
          }))
      );
    }
  }
};
